import { useState, useEffect, useCallback } from 'react';
import { WifiOff, RefreshCw, CloudUpload } from 'lucide-react';
import { offlineQueue } from '@/lib/offlineQueue';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

/** Hook that monitors online/offline state and replays queued mutations. */
export function useOfflineSync() {
  const [isOffline, setIsOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);
  const [pendingCount, setPendingCount] = useState(offlineQueue.pendingCount());
  const [isSyncing, setIsSyncing] = useState(false);
  const utils = trpc.useUtils();

  const replayQueue = useCallback(async () => {
    if (offlineQueue.pendingCount() === 0) return;
    setIsSyncing(true);

    const { replayed, failed } = await offlineQueue.replay(async (action, payload) => {
      if (action === 'habits.toggleDay') {
        // @ts-expect-error dynamic procedure call
        await utils.client.habits.toggleDay.mutate(payload);
      } else if (action === 'checkins.submit') {
        // @ts-expect-error dynamic procedure call
        await utils.client.checkins.submit.mutate(payload);
      } else if (action === 'checkins.markScripture') {
        // @ts-expect-error dynamic procedure call
        await utils.client.checkins.markScripture.mutate(payload);
      }
    });

    setIsSyncing(false);
    setPendingCount(offlineQueue.pendingCount());

    if (replayed > 0) {
      toast.success(`Synced ${replayed} offline action${replayed > 1 ? 's' : ''}`);
      utils.habits.todayLogs.invalidate();
      utils.checkins.history90.invalidate();
      utils.checkins.getStreak.invalidate();
    }
    if (failed > 0) {
      toast.error(`${failed} action${failed > 1 ? 's' : ''} failed to sync`);
    }
  }, [utils]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setTimeout(() => replayQueue(), 1500);
    };
    const handleOffline = () => {
      setIsOffline(true);
      setPendingCount(offlineQueue.pendingCount());
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [replayQueue]);

  return { isOffline, pendingCount, isSyncing, retrySync: replayQueue };
}

export function OfflineBanner() {
  const { isOffline, pendingCount, isSyncing, retrySync } = useOfflineSync();

  if (!isOffline && !isSyncing && pendingCount === 0) return null;

  const title = isSyncing ? 'Syncing saved changes' : isOffline ? 'Working offline' : 'Changes waiting to sync';
  const detail = pendingCount > 0
    ? `${pendingCount} saved change${pendingCount === 1 ? '' : 's'} waiting`
    : 'Changes will sync when the connection returns.';

  return (
    <div
      role="status"
      aria-live="polite"
      data-standard-offline-banner
      className="fixed inset-x-0 top-0 z-[9999] flex min-h-12 items-center justify-center gap-3 border-b border-white/10 bg-[#171715] px-4 py-2 text-white shadow-[0_8px_24px_rgba(0,0,0,0.24)]"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)',
        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 16px)',
        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 16px)',
      }}
    >
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#2f5d73]/30 text-[#8bb2c5]">
        {isSyncing ? <RefreshCw className="size-4 animate-spin" aria-hidden="true" /> : isOffline ? <WifiOff className="size-4" aria-hidden="true" /> : <CloudUpload className="size-4" aria-hidden="true" />}
      </div>
      <div className="min-w-0 flex-1 sm:flex-none">
        <p className="text-xs font-black uppercase tracking-[0.16em]">{title}</p>
        <p className="truncate text-[11px] text-white/60">{detail}</p>
      </div>
      {!isOffline && !isSyncing && pendingCount > 0 ? (
        <button
          type="button"
          onClick={() => void retrySync()}
          className="min-h-11 shrink-0 rounded-xl border border-[#b28a49] px-3 text-xs font-black uppercase tracking-[0.12em] text-[#d9bd87] transition-colors hover:bg-[#b28a49]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b28a49]"
        >
          Sync now
        </button>
      ) : null}
    </div>
  );
}
